import React, { useState, useEffect } from 'react';
import { Row, Col, Input } from 'reactstrap';
import { PrimaryButton } from '../../../utils/budget-tool-specific';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { collection, setDoc, addDoc, updateDoc, deleteDoc, doc, onSnapshot, getDocs, getDoc } from 'firebase/firestore';
import { Container, ActionButtonContainer, SeedButton, CardsGrid } from '../passport/commonComponents';
import CountryCard from '../passport/countryCard';
import CountryDetails from './CountryDetails';
import { countries as countriesData } from './countries';
import styled from 'styled-components';

// Dynamically import all country JSON files from the directory
const countryContext = require.context(
  '../passport/countrySeedData',
  false,
  /\.json$/
);
const countryFiles = countryContext.keys().map(countryContext);

const SettingsCountry = () => {
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [passports, setPassports] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const db = GetDB();

    useEffect(() => {
        const unsubscribeCountries = onSnapshot(doc(db, 'data', 'countries_metadata'), async (metadataDoc) => {
            if (!metadataDoc.exists()) {
                console.log('No countries metadata found');
                return;
            }

            const metadata = metadataDoc.data();
            const regions = metadata.regions || [];

            // Fetch all region documents
            const regionPromises = regions.map(region => 
                getDoc(doc(db, 'data', `countries_${region.toLowerCase()}`))
            );

            try {
                const regionDocs = await Promise.all(regionPromises);
                const allCountries = {};

                regionDocs.forEach((regionDoc, index) => {
                    if (regionDoc.exists()) {
                        const regionData = regionDoc.data();
                        Object.entries(regionData.countries).forEach(([countryId, countryData]) => {
                            allCountries[countryId] = {
                                id: countryId,
                                ...countryData
                            };
                        });
                    }
                });

                // Convert to array for the existing state
                const countryArray = Object.values(allCountries);
                // Sort countries alphabetically by name
                const sortedCountries = countryArray.sort((a, b) => a.country.localeCompare(b.country));
                console.log('Fetched countries data:', sortedCountries.map(c => ({ id: c.id, country: c.country, weight: c.internalWeight })));
                setCountries(sortedCountries);
                
                // If there's a selected country, update it with the new data
                if (selectedCountry) {
                    const updatedSelectedCountry = countryArray.find(country => country.id === selectedCountry.id);
                    if (updatedSelectedCountry) {
                        console.log('Updating selected country:', {
                            before: { id: selectedCountry.id, weight: selectedCountry.internalWeight },
                            after: { id: updatedSelectedCountry.id, weight: updatedSelectedCountry.internalWeight }
                        });
                        // Only update if the weight has actually changed in the database
                        if (updatedSelectedCountry.internalWeight !== selectedCountry.internalWeight) {
                            setSelectedCountry(updatedSelectedCountry);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        });

        // Update passport fetching to use the new data structure
        const unsubscribePassports = onSnapshot(doc(db, 'data', 'countries_metadata'), async (metadataDoc) => {
            if (!metadataDoc.exists()) {
                console.log('No countries metadata found');
                return;
            }

            const metadata = metadataDoc.data();
            const regions = metadata.regions || [];

            // Fetch all region documents
            const regionPromises = regions.map(region => 
                getDoc(doc(db, 'data', `countries_${region.toLowerCase()}`))
            );

            try {
                const regionDocs = await Promise.all(regionPromises);
                const passportData = [];

                regionDocs.forEach((regionDoc) => {
                    if (regionDoc.exists()) {
                        const regionData = regionDoc.data();
                        Object.entries(regionData.countries).forEach(([countryId, countryData]) => {
                            passportData.push({
                                value: countryId,
                                label: countryData.country
                            });
                        });
                    }
                });

                // Sort passports alphabetically by country name
                const sortedPassports = passportData.sort((a, b) => a.label.localeCompare(b.label));
                console.log('Fetched passports:', sortedPassports);
                setPassports(sortedPassports);
            } catch (error) {
                console.error('Error fetching passports:', error);
            }
        });

        return () => {
            unsubscribeCountries();
            unsubscribePassports();
        };
    }, []); // Remove selectedCountry from dependencies

    const handleAddCountry = async () => {
        const newCountry = {
            country: 'New Country',
            noVisaRequired: [],
            visaOnArrival: [],
            eVisaRequired: [],
            paperVisaRequired: [],
            restrictedPassports: [],
            information: '',
            notes: '',
            internalWeight: 1
        };

        try {
            await addDoc(collection(db, 'Countries'), newCountry);
        } catch (error) {
            console.error("Error adding country:", error);
        }
    };

    const verifyImport = async (countriesByRegion) => {
        try {
            console.log('\nStarting verification process...');
            let verificationErrors = [];
            let totalVerified = 0;

            // Get the metadata document
            const metadataDoc = await getDoc(doc(db, 'data', 'countries_metadata'));
            if (!metadataDoc.exists()) {
                throw new Error('Metadata document not found');
            }

            const metadata = metadataDoc.data();
            const regions = metadata.regions;

            // Verify each region
            for (const region of regions) {
                const regionDoc = await getDoc(doc(db, 'data', `countries_${region.toLowerCase()}`));
                if (!regionDoc.exists()) {
                    verificationErrors.push(`Region document not found: ${region}`);
                    continue;
                }

                const importedCountries = regionDoc.data().countries;
                const originalCountries = countriesByRegion[region];

                // Compare counts
                if (Object.keys(importedCountries).length !== Object.keys(originalCountries).length) {
                    verificationErrors.push(`Country count mismatch in ${region}: 
                        Imported: ${Object.keys(importedCountries).length}, 
                        Original: ${Object.keys(originalCountries).length}`);
                }

                // Compare each country's data
                for (const [countryCode, originalData] of Object.entries(originalCountries)) {
                    const importedData = importedCountries[countryCode];
                    if (!importedData) {
                        verificationErrors.push(`Country not found in ${region}: ${countryCode}`);
                        continue;
                    }

                    // Compare all fields
                    const fieldsToCompare = [
                        'country', 'region', 'information', 'internalWeight',
                        'noVisaRequired', 'visaOnArrival', 'eVisaRequired',
                        'paperVisaRequired', 'restrictedPassports'
                    ];

                    for (const field of fieldsToCompare) {
                        const originalValue = JSON.stringify(originalData[field]);
                        const importedValue = JSON.stringify(importedData[field]);
                        if (originalValue !== importedValue) {
                            console.log(`Field mismatch in ${region}/${countryCode}/${field}:`, {
                                original: originalData[field],
                                imported: importedData[field]
                            });
                            verificationErrors.push(
                                `Data mismatch in ${region}/${countryCode}/${field}:
                                Original: ${originalValue}
                                Imported: ${importedValue}`
                            );
                        }
                    }
                    totalVerified++;
                }
            }

            if (verificationErrors.length === 0) {
                console.log(`✅ Verification successful! Verified ${totalVerified} countries across ${regions.length} regions`);
                return true;
            } else {
                console.error('❌ Verification failed with the following errors:');
                verificationErrors.forEach(error => console.error(error));
                return false;
            }
        } catch (error) {
            console.error('Error during verification:', error);
            return false;
        }
    };

    const importAllCountries = async () => {
        try {
            console.log('Starting import of all countries...');
            
            // Safety check - verify data collection doesn't exist or is empty
            const dataCollection = collection(db, 'data');
            const dataSnapshot = await getDocs(dataCollection);
            if (!dataSnapshot.empty) {
                console.log('⚠️ Warning: Data collection already exists. This will overwrite existing data.');
                if (!window.confirm('The data collection already exists. This will overwrite all existing data. Continue?')) {
                    console.log('Import cancelled by user');
                    return;
                }
            }

            // Build countryDataMap from all imported country files
            const countryDataMap = {};
            for (const countryData of countryFiles) {
                if (!countryData) continue; // Skip if file doesn't exist
                
                // Get the country code from the filename
                const fileName = countryContext.keys().find(key => countryContext(key) === countryData);
                const countryCode = fileName ? fileName.split('/').pop().replace('.json', '').toUpperCase() : null;
                
                if (!countryCode || !countryData.country || !countryData.region) {
                    console.warn(`⚠️ Skipping invalid country data:`, countryData);
                    continue;
                }

                countryDataMap[countryData.country.toLowerCase().replace(/\s+/g, '-')] = {
                    data: countryData,
                    code: countryCode
                };
            }

            console.log(`Preparing to import ${Object.keys(countryDataMap).length} countries...`);

            // Group countries by region
            const countriesByRegion = {};
            let totalCountries = 0;
            let totalFields = 0;

            for (const [countryKey, { data: countryData, code: countryCode }] of Object.entries(countryDataMap)) {
                try {
                    const region = countryData.region || 'UNKNOWN';
                    if (!countriesByRegion[region]) {
                        countriesByRegion[region] = {};
                    }

                    // Transform the data
                    const transformedData = {
                        country: countryData.country,
                        region: countryData.region,
                        information: countryData.information || '',
                        noVisaRequired: countryData.noVisaRequired || [],
                        visaOnArrival: countryData.visaOnArrival || [],
                        eVisaRequired: countryData.eVisaRequired || [],
                        paperVisaRequired: countryData.paperVisaRequired || [],
                        restrictedPassports: countryData.restrictedPassports || [],
                        internalWeight: typeof countryData.internalWeight === 'number' ? countryData.internalWeight : 1
                    };

                    console.log(`Processing ${countryKey} (${countryCode}):`, {
                        originalWeight: countryData.internalWeight,
                        transformedWeight: transformedData.internalWeight
                    });

                    countriesByRegion[region][countryCode] = transformedData;
                    totalCountries++;
                    totalFields += Object.keys(transformedData).length;

                    console.log(`✅ Processed ${countryKey} (${countryCode}) for region ${region}`);
                } catch (error) {
                    console.error(`❌ Error processing ${countryKey}:`, error);
                }
            }

            // Create documents for each region
            const regionPromises = Object.entries(countriesByRegion).map(async ([region, countries]) => {
                const regionRef = doc(db, 'data', `countries_${region.toLowerCase()}`);
                console.log(`Writing ${region} region data:`, {
                    sampleCountry: Object.entries(countries)[0],
                    hasInternalWeight: Object.values(countries).every(c => typeof c.internalWeight === 'number')
                });
                await setDoc(regionRef, {
                    countries,
                    metadata: {
                        region,
                        lastUpdated: new Date().toISOString(),
                        version: 1,
                        totalCountries: Object.keys(countries).length,
                        schema: {
                            country: 'string',
                            noVisaRequired: 'array',
                            visaOnArrival: 'array',
                            eVisaRequired: 'array',
                            paperVisaRequired: 'array',
                            restrictedPassports: 'array',
                            information: 'string',
                            internalWeight: 'number',
                            region: 'string'
                        }
                    }
                });
                console.log(`✅ Successfully imported ${region} region (${Object.keys(countries).length} countries)`);
            });

            // Create a master document with metadata
            const masterRef = doc(db, 'data', 'countries_metadata');
            await setDoc(masterRef, {
                regions: Object.keys(countriesByRegion),
                totalCountries,
                totalFields,
                lastUpdated: new Date().toISOString(),
                version: 1
            });

            await Promise.all(regionPromises);
            console.log('\nImport Summary:');
            console.log(`Total countries processed: ${totalCountries}`);
            console.log(`Regions created: ${Object.keys(countriesByRegion).join(', ')}`);

            // Verify the import
            const verificationResult = await verifyImport(countriesByRegion);
            if (verificationResult) {
                console.log('✅ Import and verification completed successfully');
            } else {
                console.error('❌ Import completed but verification failed');
            }
        } catch (error) {
            console.error('Error during import:', error);
        }
    };

    const migrateToSingleDocument = async () => {
        try {
            console.log('Starting migration to single document...');
            
            // Get all countries from the Countries collection
            const countriesSnapshot = await getDocs(collection(db, 'Countries'));
            const countriesByRegion = {};
            let totalCountries = 0;
            let totalFields = 0;
            
            // Group countries by region
            countriesSnapshot.forEach((doc) => {
                const countryData = doc.data();
                const region = countryData.region || 'UNKNOWN';
                
                if (!countriesByRegion[region]) {
                    countriesByRegion[region] = {};
                }
                
                countriesByRegion[region][doc.id] = {
                    ...countryData,
                    // Add any missing fields with default values
                    noVisaRequired: countryData.noVisaRequired || [],
                    visaOnArrival: countryData.visaOnArrival || [],
                    eVisaRequired: countryData.eVisaRequired || [],
                    paperVisaRequired: countryData.paperVisaRequired || [],
                    restrictedPassports: countryData.restrictedPassports || [],
                    information: countryData.information || '',
                    internalWeight: typeof countryData.internalWeight === 'number' ? countryData.internalWeight : 1
                };
                totalCountries++;
                totalFields += Object.keys(countryData).length;
            });

            // Validate we have data
            if (totalCountries === 0) {
                throw new Error('No countries found to migrate');
            }

            // Create documents for each region
            const migrationPromises = Object.entries(countriesByRegion).map(async ([region, countries]) => {
                const regionRef = doc(db, 'data', `countries_${region.toLowerCase()}`);
                await setDoc(regionRef, {
                    countries,
                    metadata: {
                        region,
                        lastUpdated: new Date().toISOString(),
                        version: 1,
                        totalCountries: Object.keys(countries).length,
                        schema: {
                            country: 'string',
                            noVisaRequired: 'array',
                            visaOnArrival: 'array',
                            eVisaRequired: 'array',
                            paperVisaRequired: 'array',
                            restrictedPassports: 'array',
                            information: 'string',
                            internalWeight: 'number',
                            region: 'string'
                        }
                    }
                });
                console.log(`✅ Successfully migrated ${region} region (${Object.keys(countries).length} countries)`);
            });

            // Create a master document with metadata
            const masterRef = doc(db, 'data', 'countries_metadata');
            await setDoc(masterRef, {
                regions: Object.keys(countriesByRegion),
                totalCountries,
                totalFields,
                lastUpdated: new Date().toISOString(),
                version: 1
            });

            await Promise.all(migrationPromises);
            console.log('✅ Successfully completed migration');
            console.log(`Total countries migrated: ${totalCountries}`);
            console.log(`Regions processed: ${Object.keys(countriesByRegion).join(', ')}`);
        } catch (error) {
            console.error('❌ Error during migration:', error);
        }
    };

    const handleCountrySelect = (country) => {
        console.log('Selecting country:', { id: country.id, country: country.country, weight: country.internalWeight });
        setSelectedCountry(country);
    };

    const handleClose = () => {
        setSelectedCountry(null);
    };

    const filteredCountries = countries.filter(country =>
        country?.country?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <h4>Country Database</h4>
                </Col>
                <Col className="text-end">
                    <ActionButtonContainer>
                        <PrimaryButton onClick={handleAddCountry}>
                            Add Country
                        </PrimaryButton>
                        <SeedButton onClick={importAllCountries}>
                            Import All Countries
                        </SeedButton>
                        <PrimaryButton onClick={migrateToSingleDocument} style={{ marginLeft: '10px' }}>
                            Migrate to Single Document
                        </PrimaryButton>
                    </ActionButtonContainer>
                </Col>
            </Row>

            <SearchContainer>
                <SearchInput 
                    type="text"
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </SearchContainer>

            <ContentRow>
                <CountriesList>
                    <CardsGrid>
                        {filteredCountries.map((country) => (
                            <CountryCard
                                key={country.id}
                                country={country}
                                isSelected={selectedCountry?.id === country.id}
                                onSelect={() => handleCountrySelect(country)}
                            />
                        ))}
                    </CardsGrid>
                </CountriesList>

                {selectedCountry && (
                    <CountryDetails
                        country={selectedCountry}
                        passports={passports}
                        onClose={handleClose}
                    />
                )}
            </ContentRow>
        </Container>
    );
};

const ContentRow = styled.div`
    display: flex;
    gap: 20px;
    min-height: calc(100vh - 200px);
`;

const CountriesList = styled.div`
    flex: 1;
    padding-right: 10px;
`;

const SearchContainer = styled.div`
    margin-bottom: 20px;
`;

const SearchInput = styled(Input)`
    max-width: 300px;
    border-radius: 4px;
    padding: 8px 12px;
    
    &:focus {
        border-color: ${props => props.theme.primaryAccent};
        box-shadow: 0 0 0 1px ${props => props.theme.primaryAccent};
        outline: none;
    }
`;

export default SettingsCountry;